import Table from '@creator/ui/components/Table/Table';
import Tbody from '@creator/ui/components/Table/Tbody/Tbody';
import Td from '@creator/ui/components/Table/Td/Td';
import Thead from '@creator/ui/components/Table/Thead/Thead';
import Tr from '@creator/ui/components/Table/Tr/Tr';
import Text from '@creator/ui/components/Text/Text';
import { useReportsByIds } from '@src/store/reports';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ReportRow from './ReportRow';
import ReportTableHeader from './ReportTableHeader';

interface ReportTableProps {
    reportIds: string[];
    tokenName: string;
    loadStatus: 'loading' | 'success' | 'error' | 'idle';
    selectedUser: string;
    revealedReports: string[];
    onSelectUser: (username: string) => void;
    onSelectPostId: (postId: string, title: string, commentId?: string) => void;
    onModeratorRevealUserClick: (encryptedUser: string, reportId: string, tokenName: string) => Promise<void>;
    selectedType: string;
    selectedStatus: string;
    onStatusChange: (status: string) => void;
    onTypeChange: (type: string) => void;
}

const ReportTable: FC<ReportTableProps> = ({
    reportIds,
    tokenName,
    loadStatus,
    selectedUser,
    revealedReports,
    onSelectUser,
    onSelectPostId,
    onModeratorRevealUserClick,
    selectedType,
    selectedStatus,
    onStatusChange,
    onTypeChange,
}) => {
    const { t } = useTranslation();

    // Get actual report objects from IDs
    const reports = useReportsByIds(reportIds);

    function renderTableBody() {
        if (reports.length === 0 && loadStatus === 'success') {
            return (
                <Tbody>
                    <Tr>
                        <Td colSpan={7} className="py-8 text-center">
                            <Text className="text-gray-500 dark:text-gray-400">{t('tokenPage.reports.noDataMessage')}</Text>
                        </Td>
                    </Tr>
                </Tbody>
            );
        }

        return (
            <Tbody>
                {reports.map(
                    (report, index) =>
                        report && (
                            <ReportRow
                                key={report.id}
                                report={report}
                                index={index}
                                tokenName={tokenName}
                                selectedUser={selectedUser}
                                revealedReports={revealedReports}
                                onSelectUser={onSelectUser}
                                onSelectPostId={onSelectPostId}
                                onModeratorRevealUserClick={onModeratorRevealUserClick}
                            />
                        )
                )}
            </Tbody>
        );
    }

    return (
        <Table className="w-full overflow-hidden">
            <Thead className="bg-gray-50 dark:bg-gray-800">
                <ReportTableHeader selectedType={selectedType} selectedStatus={selectedStatus} onStatusChange={onStatusChange} onTypeChange={onTypeChange} />
            </Thead>
            {renderTableBody()}
        </Table>
    );
};

export default ReportTable;
